
import {
  computed, ComputedRef, defineComponent, Ref, ref, watch, onBeforeUnmount
} from 'vue';
import {
  getTemplateIframeDocument,
  getTemplateParentTypeByType,
  isDisplayTemplate,
  isEmailTemplate,
  isFacebookTemplate,
  isPushTemplate,
  isSmsTemplate,
  isRichTextingTemplate,
  isWhatsappTemplate,
  isTeaserTemplate,
  reconstructDynamicPersistentMobileCss,
} from '@/components/template-builder/utils/helpers';
import { UserState } from '@/composables/User';
import {
  Section,
  TemplateParentTypeEnum,
  TemplateStructureEnum,
} from '@/types';
import { asInt } from '@/helpers';
import { isMobileViewportWidth } from '@/configs/editor-devices';
import {
  backwardCompatibilityMediaRestrictionClasses,
  checkMultipleClassesSelectors,
  fixBrokenSmartProductsLists,
  fixBrokenWidgetVoucher,
  getActiveTemplateFullHtml,
  getActiveTemplateStylesheets,
  getTranslationIdFromImportedTemplateElement,
  hideIframeTemplateWrapper,
  insertEmbedTemplateInDom,
  removeBrFromStylesheets,
  resetActiveSection,
  resetIframe,
  resetTemplateToDesktop,
  setSelectedTranslationId,
  setTemplateConfigurationKeyInState,
  showIframeTemplateWrapper,
  templateChecksOnOpening,
  TemplateEditorState as state,
  updateSectionsInState,
  replaceSectionContent,
  refreshAttributeInDOM,
  fixTranslationsHrefAttribute,
  checkResponsiveClasses,
  TemplateEditorState,
} from '@/composables/template-editor/TemplateEditor';
import { TemplateTypeEnum } from '@/composables/shop/Templates';
import Loader from '@/components/layout/Loader.vue';
import {
  HIDE_MOBILE_ATTRIBUTE,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_NAME,
} from '@/components/template-builder/utils/constants';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/i18n';
import { useStore } from '@/store';
import {
  cleanupDynamicStyle,
} from '@/components/template-builder/utils/parser';
import { fixDataRedirect, removeTranslationInState } from './utils/translate';
import { useGoogleFonts } from '@/composables/google-fonts/useGoogleFonts';
import { processVariableImagesOnLoad, initPushDefaultIconUrl } from './utils/variable-image';
import { initAllWidgets } from '@/components/template-builder/utils/widget-init-registry';

export default defineComponent({
  name: 'TemplateLiveView',

  components: {
    Loader,
  },

  directives: {
    'iframe-listener': {
      mounted(el, binding) {
        hideIframeTemplateWrapper();
        // Check if binding value is a function
        if (typeof binding.value === 'function') {
           // Only add listener if we have a valid handler.
           // Note: The previous code had a bug where it added listener even if binding.value was not function (though it was passed).
           // But careful: el is the iframe. The listeners are on contentWindow.
           // If iframe not loaded, contentWindow might be null or not ready?
           // The directive mounts on the iframe element.

           // We'll keep original logic but ensure safety
           if (el.contentWindow) {
             el.contentWindow.addEventListener('mouseup', binding.value);
           }
        }
      },

      beforeUnmount(el, binding) {
        if (el.contentWindow && typeof binding.value === 'function') {
          el.contentWindow.removeEventListener('mouseup', binding.value);
        }
      },
    },
  },

  props: {
    templateId: {
      type: Number,
      required: true,
    },

    templateType: {
      type: String,
      required: true,
    },

    width: {
      type: String,
      required: true,
    },
  },

  emits: {
    'on-iframe-loaded': null,
  },

  setup(props, context) {
    const store = useStore();
    const { scanAndInjectGoogleFonts } = useGoogleFonts();
    const templateTitle = `template-${props.templateId}`;
    const iframe = ref<HTMLIFrameElement>(document.createElement('iframe'));
    const content: Ref<Document | null> = ref(null);
    const srcDoc = computed(() => content.value && content.value.documentElement.outerHTML);
    const isDisplay: ComputedRef<boolean> = computed(() => isDisplayTemplate(props.templateType));
    const urlShop: Ref<string> = ref(UserState.activeShop?.url ?? '');
    const loading: Ref<boolean> = ref(true);
    const loadDebugTickCount: Ref<number> = ref(0);

    const isMobile = computed(() => isMobileViewportWidth(props.width));

    const wrapperComponent = computed(() => 'div');

    const iframeStyle = computed(() => ({ width: props.width }));

    /**
     * Remove mso-line-height-alt styles in style elements
     */
    const removeMsoLineHeightAltPropertyFromStyles = (template: Document) => {
      // Get styles of template
      const stylesOfDocument = template.querySelectorAll('style');

      if (stylesOfDocument && stylesOfDocument.length > 0) {
        Array.from(stylesOfDocument).forEach((styleOfDocument) => {
          let cssRules = styleOfDocument.innerText;
          cssRules = cssRules.replace(new RegExp('mso-line-height-alt:[\\\\s]*(?!0(%|px|!))[0-9]+(!important|px|%);?', 'gi'), '');
          // eslint-disable-next-line no-param-reassign
          styleOfDocument.innerText = cssRules;
        });
      }

      // Reset current active section if any and update all sections in state
      resetActiveSection();
      updateSectionsInState();
    };

    /**
     * Remove useless elements from template based on shop configuration
     * This function centralizes all conditional element removals
     */
    const removeUselessElements = () => {
      const template = getTemplateIframeDocument();
      if (!template) {
        return;
      }

      // V3-only: V4 editor handles the create account widget separately.
      const isV4 = state.template?.version === 4 || state.template?.type === 'teaser';
      if (isV4) {
        return;
      }

      const isShopify = UserState.activeShop?.solutionEcommerce === 'shopify';

      // Shopify: Remove password field from create account widget
      // Shopify handles authentication, so password field is useless
      if (isShopify) {
        const passwordField = template.querySelector('#spm_form_registration_password');
        if (passwordField) {
          const parentFormField = passwordField.closest('.spm_form_field');
          if (parentFormField) {
            parentFormField.remove();
          }
        }
      }
    };

    /**
     * A popup close button must never carry a real link. A translated href
     * could historically leak onto `.spm_close_button` (data-spmtranslationid-href),
     * turning "close" into a redirect. On load we strip any real href from close
     * buttons and drop the stale translation so it is not re-applied on save.
     */
    const removeCloseButtonLink = () => {
      const template = getTemplateIframeDocument();
      if (!template) {
        return;
      }

      const closeButtons = template.querySelectorAll<HTMLElement>('.spm_close_button');
      closeButtons.forEach((button) => {
        const href = button.getAttribute('href');
        // Keep harmless in-template anchors (#spm_close / #spmClosePopin), strip real links.
        if (href && !href.startsWith('#')) {
          button.removeAttribute('href');
        }

        const translationKey = button.getAttribute('data-spmtranslationid-href');
        if (translationKey) {
          button.removeAttribute('data-spmtranslationid-href');
          removeTranslationInState(translationKey.replace('LANG_', ''));
        }
      });
    };

    /**
     * Execute functions after loading a display template
     */
    const applyPostLoadingChecksForDisplay = () => {
      const template = getTemplateIframeDocument();

      if (template) {
        fixBrokenSmartProductsLists();
        fixBrokenWidgetVoucher();
        cleanupDynamicStyle();
        fixDataRedirect(template);
        removeCloseButtonLink();
        removeUselessElements();

        // Scan and inject Google Fonts used in the template
        const detectedFonts = scanAndInjectGoogleFonts(template);

        // Initialize all widgets that have registered init handlers (countdown, etc.)
        initAllWidgets('load', getTemplateParentTypeByType(state.template?.type));
      }
    };

    /**
     * Execute functions after loading an email template
     */
    const applyPostLoadingChecksForEmail = () => {
      const template = getTemplateIframeDocument();

      if (template) {
        if (!TemplateEditorState?.template?.informations.imported) {
          removeMsoLineHeightAltPropertyFromStyles(template);
          backwardCompatibilityMediaRestrictionClasses(template);
          cleanupDynamicStyle();
          reconstructDynamicPersistentMobileCss();
          checkMultipleClassesSelectors();
          fixBrokenSmartProductsLists();
          removeBrFromStylesheets();
          checkResponsiveClasses(template);
        }

        const { lang } = UserState.user;
        const languageToUse = lang && SUPPORTED_LOCALES.includes(lang) ? lang : DEFAULT_LOCALE;

        refreshAttributeInDOM(languageToUse);
        fixTranslationsHrefAttribute();
        fixDataRedirect(template);

        // Scan and inject Google Fonts used in the email template
        scanAndInjectGoogleFonts(template);

        // Initialize all widgets that have registered init handlers (enkore, etc.)
        initAllWidgets('load', getTemplateParentTypeByType(state.template?.type));
      }
    };

    const createIframeContent = async () => {
      // add html to body
      const spmTemplate = document.createElement('spm-template');
      spmTemplate.innerHTML = getActiveTemplateFullHtml();

      content.value = document.implementation.createHTMLDocument();

      if (isDisplay.value) {
        // Get default url from template
        let url = spmTemplate?.querySelector('#spm_body')?.getAttribute('data-target-url');

        if (url) {
          url = atob(url);

          // We check if the url doesn't belong to the shop, if so we change the target-url
          if (!(new RegExp(urlShop.value.replace(new RegExp('^https?:'), ''))).test(url.replace(new RegExp('^https?:'), ''))) {
            const body = spmTemplate?.querySelector('#spm_body');
            if (body) {
              body.setAttribute('data-target-url', btoa(urlShop.value));
            }
          }
        }

        // Add extra css and js to iframe
        const spmApiCss = document.createElement('link');
        spmApiCss.setAttribute('rel', 'stylesheet');
        spmApiCss.setAttribute('data-do-not-rewrite-url', '1');
        // Load V4 CSS for version 4 templates (includes teasers which are always V4), V3 CSS otherwise
        const spmBody = spmTemplate?.querySelector('[id^=spm_body]');
        const isV4Template = spmBody?.classList.contains('/') || state.template?.version === 4;
        const cssPath = isV4Template ? `${process.env.VUE_APP_NESTJS_TRACKING_API_URL}/api/assets/css/v4/all.css` : '/css/SpmAPI.css';
        spmApiCss.setAttribute('href', `${cssPath}`);
        content.value.head.appendChild(spmApiCss);
        const timestamp = new Date().getTime(); // Génère un timestamp unique
        const fixSpmTemplateCss = document.createElement('link');
        fixSpmTemplateCss.setAttribute('rel', 'stylesheet');
        fixSpmTemplateCss.setAttribute('data-do-not-rewrite-url', '1');
        fixSpmTemplateCss.setAttribute('href', `/css/template-builder/fixSpmTemplate.css?${timestamp}`);
        content.value.head.appendChild(fixSpmTemplateCss);

        const spmApiJs = document.createElement('script');
        spmApiJs.setAttribute('data-do-not-rewrite-url', '1');
        spmApiJs.setAttribute('src', 'https://app-spm.com/procedural_api/SpmAPI.min.js');
        content.value.head.appendChild(spmApiJs);

        // Games CSS is already included in V4 all.css (games module), no need to load separately

        const baseGameJs = document.createElement('script');
        baseGameJs.setAttribute('data-do-not-rewrite-url', '1');
        baseGameJs.setAttribute('src', `${process.env.VUE_APP_NESTJS_TRACKING_API_URL}/api/assets/js/spm-games.js?v=${Date.now()}`);
        content.value.head.appendChild(baseGameJs);

        const countdownJs = document.createElement('script');
        countdownJs.setAttribute('data-do-not-rewrite-url', '1');
        countdownJs.setAttribute('src', `${process.env.VUE_APP_NESTJS_TRACKING_API_URL}/api/assets/js/spm-countdown.js?v=${Date.now()}`);
        content.value.head.appendChild(countdownJs);
      }

      const { lang } = UserState.user;
      const languageToUse = lang && SUPPORTED_LOCALES.includes(lang) ? lang : DEFAULT_LOCALE;

      // Add base CSS styles for live edit
      const baseIframeCss = document.createElement('link');
      baseIframeCss.setAttribute('rel', 'stylesheet');
      baseIframeCss.setAttribute('data-do-not-rewrite-url', '1');
      baseIframeCss.setAttribute('href', '/css/template-builder/baseIframe.css?20260123y');
      content.value.head.appendChild(baseIframeCss);

      if (getTemplateParentTypeByType(props.templateType) === TemplateParentTypeEnum.EMBED) {
        const embedStyle = document.createElement('style');
        embedStyle.setAttribute('type', 'text/css');
        embedStyle.innerHTML = `
          body {
            display: flex !important;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            margin: 0 !important;
            background: #D5D5D5 !important;
          }
          spm-template {
            border-top: 10px solid #e8e8e8;
            border-bottom: 10px solid #e8e8e8;
            width:100%;
            position: relative;
          }
          spm-template::after {
            content: '';
            position: absolute;
            top: calc(100% + 10px);
            left: 50%;
            transform: translateX(-50%);
            width: 10px;
            height: 100vh;
            background: #e8e8e8;
            pointer-events: none;
          }
          .spm_display_V3 [id^=spm_content] { padding-right: 0; }
          #spm_body { box-shadow: 10px 0 0 0 #e8e8e8, -10px 0 0 0 #e8e8e8; }
        `;
        content.value.head.appendChild(embedStyle);
      } else if (getTemplateParentTypeByType(props.templateType) === TemplateParentTypeEnum.POPUP) {
        const popupStyle = document.createElement('style');
        popupStyle.setAttribute('type', 'text/css');
        // eslint-disable-next-line max-len
        popupStyle.innerHTML = '.spm_display_V3 #spm_content { box-shadow: 0px 0px 13px 6px rgba(166,166,166,0.77); } [id^=spm_body].spm_display_V3 .spm_display_overlay { background-color: #fff; opacity: 0; }';
        content.value.head.appendChild(popupStyle);

        // Inject scroll handling script for popups taller than the visible area
        // Replicates the production client-side scroll behavior inside the editor iframe.
        // On client sites, the page behind the fixed popup provides scroll height.
        // In the editor iframe there is no page content, so we create a spacer element
        // on the body to make window.scroll fire, then adjust spm_content top position.
        const popupScrollScript = document.createElement('script');
        popupScrollScript.textContent = `
          (function () {
            var ScrollDirection = { SCROLLUP: -1, NOSCROLL: 0, SCROLLDOWN: 1 };
            var scrollState = { currentPosition: 0, oldPosition: 0, status: ScrollDirection.NOSCROLL, ticking: false };

            function getPopinContentElement() {
              return document.querySelector('div.spm_popin div[id*="spm_content"]')
                || document.querySelector('[id^=spm_body].spm_popin [id^=spm_content]');
            }

            function getWindowHeight() {
              return document.documentElement.clientHeight;
            }

            function toPx(value) {
              return Number(value) + 'px';
            }

            function getScrollDifference() {
              return Math.abs(scrollState.oldPosition - scrollState.currentPosition);
            }

            function adjustPopinPosition() {
              var popinContent = getPopinContentElement();
              if (!popinContent) return;

              if (popinContent.style.position !== 'relative') {
                popinContent.style.position = 'relative';
              }

              if (popinContent.scrollHeight > getWindowHeight()) {
                if (scrollState.currentPosition > 0) {
                  if (scrollState.status === ScrollDirection.SCROLLDOWN) {
                    if (popinContent.getBoundingClientRect().bottom >= getWindowHeight()) {
                      popinContent.style.top = toPx(
                        popinContent.getBoundingClientRect().top - getScrollDifference()
                      );
                    }
                  } else if (popinContent.getBoundingClientRect().top < 0) {
                    popinContent.style.top = toPx(
                      popinContent.getBoundingClientRect().top + getScrollDifference()
                    );
                  }
                }

                if (popinContent.getBoundingClientRect().top > 0 || scrollState.currentPosition === 0) {
                  popinContent.style.top = toPx(0);
                }

                if (
                  popinContent.getBoundingClientRect().bottom < getWindowHeight() ||
                  scrollState.currentPosition ===
                    Math.max.apply(null, Array.from(document.body.children).map(function(el) { return el.scrollHeight; })) - getWindowHeight()
                ) {
                  popinContent.style.top = toPx(getWindowHeight() - popinContent.getBoundingClientRect().height);
                }
              } else {
                popinContent.style.top = toPx(0);
              }
            }

            // Create a spacer element to make the iframe body scrollable.
            // On real client sites the page content behind the fixed popup provides this height.
            // We replicate that by inserting an invisible div whose height matches the popup content.
            function ensureBodyScrollable() {
              var popinContent = getPopinContentElement();
              if (!popinContent) return;

              var spacerId = 'spm_popup_scroll_spacer';
              var spacer = document.getElementById(spacerId);
              var contentHeight = popinContent.scrollHeight;
              var viewportHeight = getWindowHeight();

              if (contentHeight > viewportHeight) {
                if (!spacer) {
                  spacer = document.createElement('div');
                  spacer.id = spacerId;
                  spacer.style.cssText = 'pointer-events:none;width:1px;visibility:hidden;position:relative;z-index:-1;';
                  document.body.appendChild(spacer);
                }
                spacer.style.height = contentHeight + 'px';
              } else if (spacer) {
                spacer.style.height = '0px';
              }
            }

            window.addEventListener('scroll', function () {
              scrollState.oldPosition = scrollState.currentPosition;
              scrollState.currentPosition = window.scrollY;
              scrollState.status =
                scrollState.currentPosition < scrollState.oldPosition
                  ? ScrollDirection.SCROLLUP
                  : scrollState.oldPosition < scrollState.currentPosition
                    ? ScrollDirection.SCROLLDOWN
                    : ScrollDirection.NOSCROLL;

              if (!scrollState.ticking) {
                window.requestAnimationFrame(function () {
                  adjustPopinPosition();
                  scrollState.ticking = false;
                });
                scrollState.ticking = true;
              }
            });

            // Poll until popup content is available, then create the spacer
            var initInterval = setInterval(function () {
              var popinContent = getPopinContentElement();
              if (popinContent) {
                clearInterval(initInterval);
                ensureBodyScrollable();
                popinContent.style.removeProperty('top');

                // Re-check spacer height on window resize
                window.addEventListener('resize', ensureBodyScrollable);

                // Observe content size changes (e.g. user adds/removes elements in editor)
                if (typeof ResizeObserver !== 'undefined') {
                  new ResizeObserver(ensureBodyScrollable).observe(popinContent);
                }
              }
            }, 500);
          })();
        `;
        content.value.head.appendChild(popupScrollScript);
      }

      // Add CSS styles by lang for live edit
      const baseIframeLangCss = document.createElement('link');
      baseIframeLangCss.setAttribute('rel', 'stylesheet');
      baseIframeLangCss.setAttribute('data-do-not-rewrite-url', '1');
      baseIframeLangCss.setAttribute('href', `/css/template-builder/baseIframe-${languageToUse}.css`);
      content.value.head.appendChild(baseIframeLangCss);

      // Add CSS styles according to template's type
      if (isPushTemplate(state.template?.type)) {
        const baseIframeCssPush = document.createElement('link');
        baseIframeCssPush.setAttribute('rel', 'stylesheet');
        baseIframeCssPush.setAttribute('data-do-not-rewrite-url', '1');
        baseIframeCssPush.setAttribute('href', `/css/template-builder/baseIframePushNotifications.css?v=${Date.now()}`);
        content.value.head.appendChild(baseIframeCssPush);

        // Inject push notification smartphone structure
        // #spm_body may be a <table> or already a <div>; convert to <div> if needed
        const spmBodyEl = spmTemplate?.querySelector('#spm_body') as HTMLElement | null;
        if (spmBodyEl) {
          let spmBodyDiv: HTMLElement;
          if (spmBodyEl.tagName.toLowerCase() === 'table') {
            // Replace <table> with <div> keeping the same id and attributes
            spmBodyDiv = document.createElement('div');
            spmBodyDiv.id = 'spm_body';
            Array.from(spmBodyEl.attributes).forEach((attr) => {
              if (attr.name !== 'id') {
                spmBodyDiv.setAttribute(attr.name, attr.value);
              }
            });
            while (spmBodyEl.firstChild) {
              spmBodyDiv.appendChild(spmBodyEl.firstChild);
            }
            spmBodyEl.parentNode?.replaceChild(spmBodyDiv, spmBodyEl);
          } else {
            spmBodyDiv = spmBodyEl;
          }

          const alreadyHasNewDesign = !!spmBodyDiv.querySelector('.push-lock-screen');

          if (alreadyHasNewDesign) {
            // Already migrated: just update dynamic content (time, date)
            const now = new Date();
            const hours = now.getHours();
            const minutes = now.getMinutes() < 10 ? `0${now.getMinutes()}` : now.getMinutes();
            const timeStr = `${hours}:${minutes}`;
            const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            const timeEl = spmBodyDiv.querySelector('.push-status-bar__time');
            if (timeEl) timeEl.textContent = timeStr;
            const clockTimeEl = spmBodyDiv.querySelector('#push-display-time');
            if (clockTimeEl) clockTimeEl.textContent = timeStr;
            const clockDateEl = spmBodyDiv.querySelector('#push-display-date');
            if (clockDateEl) clockDateEl.textContent = dateStr;
            // Remove stray text nodes (e.g. "Section") and orphaned translation
            // markup from the section element — only .push-body should be a direct child
            const sectionElMigrated = spmBodyDiv.querySelector('[data-spmelementid]') as HTMLElement | null;
            if (sectionElMigrated) {
              Array.from(sectionElMigrated.childNodes).forEach((child) => {
                if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
                  child.remove();
                } else if (child.nodeType === Node.ELEMENT_NODE
                  && (child as HTMLElement).tagName?.toLowerCase() === 'spmtranslation') {
                  child.remove();
                }
              });
            }
          } else {
            // Old template: clean up leftovers and inject preview shell (one-time migration)
            // Clean up any leftover injected elements from previously corrupted saves
            const pushCleanupSelectors = [
              '.push-status-bar', '.push-lock-icon',
              '.push-clock', '.push-clock__time', '.push-clock__date',
              '#push-display-time', '#push-display-date',
              '.push-notification-card-header', '.push-home-indicator',
            ].join(', ');
            spmBodyDiv.querySelectorAll(pushCleanupSelectors).forEach((el) => el.remove());
            // Unwrap any leftover push-lock-screen wrappers (move children out, then remove)
            spmBodyDiv.querySelectorAll('.push-lock-screen').forEach((wrapper) => {
              while (wrapper.firstChild) {
                wrapper.parentNode?.insertBefore(wrapper.firstChild, wrapper);
              }
              wrapper.remove();
            });

            // Normalize push notification HTML: if the section structure is non-standard
            // (leftover preview elements, missing .push-body wrapper, etc.), extract
            // essential data and rebuild from scratch. Uses cloneNode(true) to preserve
            // all attributes (data-translation, data-redirect, src, etc.)
            const sectionEl = spmBodyDiv.querySelector('[data-spmelementid]') as HTMLElement | null;
            if (sectionEl) {
              // Remove stray text nodes directly under the section element (e.g. "Section")
              // that would be detected as translatable by generateTranslations
              Array.from(sectionEl.childNodes).forEach((child) => {
                if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
                  child.remove();
                }
              });

              const hasLeftovers = sectionEl.querySelector(
                '.push-status-bar, .push-lock-screen, .push-clock, #push-display-time, #push-display-date, .push-lock-icon, .push-home-indicator, .push-notification-card-header',
              );
              const hasPushBody = sectionEl.querySelector('.push-body');
              const hasTitle = sectionEl.querySelector('.push-notification-title, h1.push-notification-title');

              if (hasLeftovers || !hasPushBody || !hasTitle) {
                // Clone essential elements (deep clone preserves all attributes)
                const titleNode = (
                  sectionEl.querySelector('h1.push-notification-title')
                  || sectionEl.querySelector('.push-notification-title')
                )?.cloneNode(true) as HTMLElement | null;
                const bodyNode = sectionEl.querySelector('.push-notification-body')?.cloneNode(true) as HTMLElement | null;
                const iconNode = sectionEl.querySelector('.push-notification-icon')?.cloneNode(true) as HTMLElement | null;
                const imageNode = sectionEl.querySelector('.push-notification-image')?.cloneNode(true) as HTMLElement | null;
                const linkNode = sectionEl.querySelector('.push-notification-link')?.cloneNode(true) as HTMLElement | null;

                // Build clean structure
                const pushBody = document.createElement('a');
                pushBody.className = 'push-body';

                // Icon
                if (iconNode) {
                  pushBody.appendChild(iconNode);
                } else {
                  const iconDiv = document.createElement('div');
                  iconDiv.className = 'push-notification-icon';
                  iconDiv.innerHTML = '<img src="">';
                  pushBody.appendChild(iconDiv);
                }

                // Title (ensure it is an <h1> as expected by page-item-config selectors)
                if (titleNode) {
                  if (titleNode.tagName.toLowerCase() !== 'h1') {
                    const h1 = document.createElement('h1');
                    h1.className = 'push-notification-title';
                    Array.from(titleNode.attributes).forEach((attr) => {
                      if (attr.name !== 'class') h1.setAttribute(attr.name, attr.value);
                    });
                    h1.innerHTML = titleNode.innerHTML;
                    pushBody.appendChild(h1);
                  } else {
                    pushBody.appendChild(titleNode);
                  }
                } else {
                  const h1 = document.createElement('h1');
                  h1.className = 'push-notification-title';
                  pushBody.appendChild(h1);
                }

                // Body
                if (bodyNode) {
                  pushBody.appendChild(bodyNode);
                } else {
                  const div = document.createElement('div');
                  div.className = 'push-notification-body';
                  pushBody.appendChild(div);
                }

                // Image
                if (imageNode) {
                  pushBody.appendChild(imageNode);
                } else {
                  const imgDiv = document.createElement('div');
                  imgDiv.className = 'push-notification-image';
                  imgDiv.innerHTML = '<img src="">';
                  pushBody.appendChild(imgDiv);
                }

                // Link
                if (linkNode) {
                  pushBody.appendChild(linkNode);
                } else {
                  const div = document.createElement('div');
                  div.className = 'push-notification-link';
                  pushBody.appendChild(div);
                }

                // Replace section content with clean HTML
                sectionEl.innerHTML = '';
                sectionEl.appendChild(pushBody);

                // Persist normalized HTML in state so it gets saved correctly
                const sectionId = sectionEl.getAttribute('data-spmelementid');
                if (sectionId) {
                  replaceSectionContent(Number(sectionId), sectionEl.innerHTML, null, null);
                }
              }
            }

            // Now spmBodyDiv is a <div> — same approach as SMS/WhatsApp
            // Format current time and date
            const now = new Date();
            const hours = now.getHours();
            const minutes = now.getMinutes() < 10 ? `0${now.getMinutes()}` : now.getMinutes();
            const timeStr = `${hours}:${minutes}`;
            const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

            // 1. Status bar
            const statusBar = document.createElement('div');
            statusBar.className = 'push-status-bar spm_no_translate';
            statusBar.setAttribute('data-do-not-select', '1');
            statusBar.innerHTML = [
              `<span class="push-status-bar__time">${timeStr}</span>`,
              '<span class="push-status-bar__notch"></span>',
              '<span class="push-status-bar__icons">',
              '<span class="push-status-bar__icon-signal"></span>',
              '<span class="push-status-bar__icon-wifi"></span>',
              '<span class="push-status-bar__icon-battery"></span>',
              '</span>',
            ].join('');

            // 2. Lock screen wrapper — move ALL existing children into it (same as SMS)
            const lockScreen = document.createElement('div');
            lockScreen.className = 'push-lock-screen';
            lockScreen.setAttribute('data-do-not-select', '1');

            // Move all existing children into lockScreen
            while (spmBodyDiv.firstChild) {
              lockScreen.appendChild(spmBodyDiv.firstChild);
            }

            // Build and insert clock at the beginning of lockScreen
            const headerHtml = [
              '<span class="push-lock-icon spm_no_translate"></span>',
              '<div class="push-clock spm_no_translate">',
              `  <div class="push-clock__time spm_no_translate" id="push-display-time">${timeStr}</div>`,
              `  <div class="push-clock__date spm_no_translate" id="push-display-date">${dateStr}</div>`,
              '</div>',
            ].join('');

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = headerHtml;
            while (tempDiv.lastChild) {
              lockScreen.insertBefore(tempDiv.lastChild, lockScreen.firstChild);
            }

            // Assemble: status bar → lock screen → home indicator
            spmBodyDiv.appendChild(statusBar);
            spmBodyDiv.appendChild(lockScreen);
            const homeIndicator = document.createElement('span');
            homeIndicator.className = 'push-home-indicator spm_no_translate';
            spmBodyDiv.appendChild(homeIndicator);

            // Persist the migrated design in state so it survives minimize/save
            const designId = state.template?.content?.design;
            if (designId) {
              const cloned = spmBodyDiv.cloneNode(true) as HTMLElement;
              // Strip data-spmelementid from all elements to avoid duplication
              // with the separate content section from getActiveTemplateFullHtml
              cloned.querySelectorAll('[data-spmelementid]').forEach((el) => el.removeAttribute('data-spmelementid'));
              cloned.removeAttribute('data-spmelementid');
              replaceSectionContent(Number(designId), cloned.outerHTML, null, null);
            }
          }
        }
      } else if (isRichTextingTemplate(state.template?.type)) {
        const baseIframeCssRichTexting = document.createElement('link');
        baseIframeCssRichTexting.setAttribute('rel', 'stylesheet');
        baseIframeCssRichTexting.setAttribute('data-do-not-rewrite-url', '1');
        baseIframeCssRichTexting.setAttribute('href', '/css/template-builder/baseIframeRichTexting.css?v=20260309p');
        content.value.head.appendChild(baseIframeCssRichTexting);
        // Inject RCS status bar + header into #spm_body
        const spmBodyRcs = spmTemplate?.querySelector('#spm_body');
        if (spmBodyRcs) {
          const shopName = UserState.activeShop?.name ?? 'Shop';
          spmBodyRcs.setAttribute('data-shop-name', shopName);
          const alreadyHasNewDesign = !!spmBodyRcs.querySelector('.rcs-messages-area');

          if (alreadyHasNewDesign) {
            // Already migrated: just update dynamic content (shop name)
            const nameEl = spmBodyRcs.querySelector('.rcs-preview-header__name');
            if (nameEl) nameEl.textContent = shopName;
            // Ensure spm_no_translate is present on preview shell elements
            spmBodyRcs.querySelector('.rcs-status-bar')?.classList.add('spm_no_translate');
            spmBodyRcs.querySelector('.rcs-preview-header')?.classList.add('spm_no_translate');
          } else {
            // Old template: inject preview shell (one-time migration)
            // 1. Status bar with notch, time and icons
            const rcsStatusBar = document.createElement('div');
            rcsStatusBar.className = 'rcs-status-bar spm_no_translate';
            rcsStatusBar.setAttribute('data-do-not-select', '1');
            rcsStatusBar.innerHTML = [
              '<span class="rcs-status-bar__time">09:41</span>',
              '<span class="rcs-status-bar__notch"></span>',
              '<span class="rcs-status-bar__icons">',
              '<span class="rcs-status-bar__icon-signal"></span>',
              '<span class="rcs-status-bar__icon-wifi"></span>',
              '<span class="rcs-status-bar__icon-battery"></span>',
              '</span>',
            ].join('');

            // 2. RCS header (back arrow, avatar, shop name, icons)
            const rcsHeader = document.createElement('div');
            rcsHeader.className = 'rcs-preview-header spm_no_translate';
            rcsHeader.setAttribute('data-do-not-select', '1');
            rcsHeader.innerHTML = [
              '<span class="rcs-preview-header__back"></span>',
              '<span class="rcs-preview-header__avatar"></span>',
              `<span class="rcs-preview-header__name">${shopName}</span>`,
              '<span class="rcs-preview-header__icons"></span>',
            ].join('');

            // 3. Scrollable messages area — wrap all existing children
            const rcsMessagesArea = document.createElement('div');
            rcsMessagesArea.className = 'rcs-messages-area';
            rcsMessagesArea.setAttribute('data-do-not-select', '1');
            while (spmBodyRcs.firstChild) {
              rcsMessagesArea.appendChild(spmBodyRcs.firstChild);
            }

            // Re-assemble: status bar → header → scrollable messages
            spmBodyRcs.appendChild(rcsStatusBar);
            spmBodyRcs.appendChild(rcsHeader);
            spmBodyRcs.appendChild(rcsMessagesArea);

            // Persist the migrated design in state so it survives minimize/save
            const designId = state.template?.content?.design;
            if (designId) {
              const cloned = spmBodyRcs.cloneNode(true) as HTMLElement;
              cloned.querySelectorAll('[data-spmelementid]').forEach((el) => el.removeAttribute('data-spmelementid'));
              cloned.removeAttribute('data-spmelementid');
              replaceSectionContent(Number(designId), cloned.outerHTML, null, null);
            }
          }

          // Strip alt translation attribute from image (not needed for RCS)
          const rcsImg = spmBodyRcs.querySelector('img.rcs-message__image');
          if (rcsImg) {
            rcsImg.removeAttribute('data-spmtranslationid-alt');
            rcsImg.removeAttribute('alt');
          }

          // Inject title element if missing (for rich card mode)
          const rcsBody = spmBodyRcs.querySelector('.rcs-message__body');
          if (rcsBody && !rcsBody.querySelector('.rcs-message__title')) {
            const titleDiv = document.createElement('div');
            titleDiv.className = 'rcs-message__title';
            const textEl = rcsBody.querySelector('.rcs-message__text');
            if (textEl) {
              rcsBody.insertBefore(titleDiv, textEl);
            } else {
              rcsBody.prepend(titleDiv);
            }
            // Persist the injected title element
            const designId = state.template?.content?.design;
            if (designId) {
              const cloned = spmBodyRcs.cloneNode(true) as HTMLElement;
              cloned.querySelectorAll('[data-spmelementid]').forEach((el) => el.removeAttribute('data-spmelementid'));
              cloned.removeAttribute('data-spmelementid');
              replaceSectionContent(Number(designId), cloned.outerHTML, null, null);
            }
          }
        }
      } else if (isWhatsappTemplate(state.template?.type)) {
        const baseIframeCssWhatsapp = document.createElement('link');
        baseIframeCssWhatsapp.setAttribute('rel', 'stylesheet');
        baseIframeCssWhatsapp.setAttribute('data-do-not-rewrite-url', '1');
        baseIframeCssWhatsapp.setAttribute('href', '/css/template-builder/baseIframeWhatsapp.css?v=20260213f');
        content.value.head.appendChild(baseIframeCssWhatsapp);
        // Inject WhatsApp status bar + header into #spm_body
        const spmBody = spmTemplate?.querySelector('#spm_body');
        if (spmBody) {
          const shopName = UserState.activeShop?.name ?? 'Shop';
          spmBody.setAttribute('data-shop-name', shopName);
          const alreadyHasNewDesign = !!spmBody.querySelector('.whatsapp-messages-area');

          if (alreadyHasNewDesign) {
            // Already migrated: just update dynamic content (shop name)
            const nameEl = spmBody.querySelector('.whatsapp-preview-header__name');
            if (nameEl) nameEl.textContent = shopName;
            // Ensure spm_no_translate is present on preview shell elements
            spmBody.querySelector('.whatsapp-status-bar')?.classList.add('spm_no_translate');
            spmBody.querySelector('.whatsapp-preview-header')?.classList.add('spm_no_translate');
          } else {
            // Old template: inject preview shell (one-time migration)
            // 1. Status bar with notch, time and icons
            const waStatusBar = document.createElement('div');
            waStatusBar.className = 'whatsapp-status-bar spm_no_translate';
            waStatusBar.setAttribute('data-do-not-select', '1');
            waStatusBar.innerHTML = [
              '<span class="whatsapp-status-bar__time">09:41</span>',
              '<span class="whatsapp-status-bar__notch"></span>',
              '<span class="whatsapp-status-bar__icons">',
              '<span class="whatsapp-status-bar__icon-signal"></span>',
              '<span class="whatsapp-status-bar__icon-wifi"></span>',
              '<span class="whatsapp-status-bar__icon-battery"></span>',
              '</span>',
            ].join('');

            // 2. WhatsApp header (avatar, shop name, icons)
            const waHeader = document.createElement('div');
            waHeader.className = 'whatsapp-preview-header spm_no_translate';
            waHeader.setAttribute('data-do-not-select', '1');
            waHeader.innerHTML = [
              '<span class="whatsapp-preview-header__back"></span>',
              '<span class="whatsapp-preview-header__avatar"></span>',
              `<span class="whatsapp-preview-header__name">${shopName}</span>`,
              '<span class="whatsapp-preview-header__icons"></span>',
            ].join('');

            // 3. Scrollable messages area — wrap all existing children
            const messagesArea = document.createElement('div');
            messagesArea.className = 'whatsapp-messages-area';
            messagesArea.setAttribute('data-do-not-select', '1');
            while (spmBody.firstChild) {
              messagesArea.appendChild(spmBody.firstChild);
            }

            // Re-assemble: status bar → header → scrollable messages
            spmBody.appendChild(waStatusBar);
            spmBody.appendChild(waHeader);
            spmBody.appendChild(messagesArea);

            // Persist the migrated design in state so it survives minimize/save
            const designId = state.template?.content?.design;
            if (designId) {
              const cloned = spmBody.cloneNode(true) as HTMLElement;
              cloned.querySelectorAll('[data-spmelementid]').forEach((el) => el.removeAttribute('data-spmelementid'));
              cloned.removeAttribute('data-spmelementid');
              replaceSectionContent(Number(designId), cloned.outerHTML, null, null);
            }
          }
        }
      } else if (isSmsTemplate(state.template?.type)) {
        const baseIframeCssSms = document.createElement('link');
        baseIframeCssSms.setAttribute('rel', 'stylesheet');
        baseIframeCssSms.setAttribute('data-do-not-rewrite-url', '1');
        baseIframeCssSms.setAttribute('href', '/css/template-builder/baseIframeSms.css?v=20260213e');
        content.value.head.appendChild(baseIframeCssSms);
        // Inject SMS preview structure into #spm_body
        const spmBodySms = spmTemplate?.querySelector('#spm_body');
        if (spmBodySms) {
          const shopName = UserState.activeShop?.name ?? 'Shop';
          const alreadyHasNewDesign = !!spmBodySms.querySelector('.sms-messages-area');

          if (alreadyHasNewDesign) {
            // Already migrated: just update dynamic content (shop name)
            const nameEl = spmBodySms.querySelector('.sms-preview-header__name');
            if (nameEl) nameEl.textContent = shopName;
            // Ensure spm_no_translate is present on preview shell elements
            spmBodySms.querySelector('.sms-status-bar')?.classList.add('spm_no_translate');
            spmBodySms.querySelector('.sms-preview-header')?.classList.add('spm_no_translate');
          } else {
            // Old template: inject preview shell (one-time migration)
            // 1. Status bar with notch, time and icons
            const statusBar = document.createElement('div');
            statusBar.className = 'sms-status-bar spm_no_translate';
            statusBar.setAttribute('data-do-not-select', '1');
            statusBar.innerHTML = [
              '<span class="sms-status-bar__time">09:41</span>',
              '<span class="sms-status-bar__notch"></span>',
              '<span class="sms-status-bar__icons">',
              '<span class="sms-status-bar__icon-signal"></span>',
              '<span class="sms-status-bar__icon-wifi"></span>',
              '<span class="sms-status-bar__icon-battery"></span>',
              '</span>',
            ].join('');

            // 2. SMS header (back arrow, avatar, shop name)
            const smsHeader = document.createElement('div');
            smsHeader.className = 'sms-preview-header spm_no_translate';
            smsHeader.setAttribute('data-do-not-select', '1');
            smsHeader.innerHTML = `<span class="sms-preview-header__avatar"></span><span class="sms-preview-header__name">${shopName}</span>`;

            // 3. Scrollable messages area — wrap all existing children (the bubble)
            const messagesArea = document.createElement('div');
            messagesArea.className = 'sms-messages-area';
            messagesArea.setAttribute('data-do-not-select', '1');
            while (spmBodySms.firstChild) {
              messagesArea.appendChild(spmBodySms.firstChild);
            }

            // Assemble: status bar → header → messages area (phone is cut at bottom on white)
            spmBodySms.appendChild(statusBar);
            spmBodySms.appendChild(smsHeader);
            spmBodySms.appendChild(messagesArea);

            // Persist the migrated design in state so it survives minimize/save
            const designId = state.template?.content?.design;
            if (designId) {
              const cloned = spmBodySms.cloneNode(true) as HTMLElement;
              cloned.querySelectorAll('[data-spmelementid]').forEach((el) => el.removeAttribute('data-spmelementid'));
              cloned.removeAttribute('data-spmelementid');
              replaceSectionContent(Number(designId), cloned.outerHTML, null, null);
            }
          }
        }
      } else if (isFacebookTemplate(state.template?.type)) {
        const baseIframeCssFacebookMessenger = document.createElement('link');
        baseIframeCssFacebookMessenger.setAttribute('rel', 'stylesheet');
        baseIframeCssFacebookMessenger.setAttribute('data-do-not-rewrite-url', '1');
        baseIframeCssFacebookMessenger.setAttribute('href', '/css/template-builder/baseIframeFacebookMessenger.css');
        content.value.head.appendChild(baseIframeCssFacebookMessenger);
      } else if (isTeaserTemplate(state.template?.type)) {
        const baseIframeCssTeaser = document.createElement('link');
        baseIframeCssTeaser.setAttribute('rel', 'stylesheet');
        baseIframeCssTeaser.setAttribute('data-do-not-rewrite-url', '1');
        baseIframeCssTeaser.setAttribute('href', '/css/template-builder/baseIframeTeaser.css');
        content.value.head.appendChild(baseIframeCssTeaser);
        const spmFont = document.createElement('link');
        spmFont.setAttribute('rel', 'stylesheet');
        spmFont.setAttribute('data-do-not-rewrite-url', '1');
        spmFont.setAttribute('href', 'https://media.shopimind.io/app/css/spm_fonts/css/spm_font.css');
        content.value.head.appendChild(spmFont);
      } else if (isDisplayTemplate(state.template?.type)) {
        const spmFont = document.createElement('link');
        spmFont.setAttribute('rel', 'stylesheet');
        spmFont.setAttribute('data-do-not-rewrite-url', '1');
        spmFont.setAttribute('href', 'https://media.shopimind.io/app/css/spm_fonts/css/spm_font.css');
        content.value.head.appendChild(spmFont);
        const metaPolicy = document.createElement('meta');
        metaPolicy.setAttribute('http-equiv', 'Content-Security-Policy');
        metaPolicy.setAttribute('content', 'upgrade-insecure-requests');
        content.value.head.appendChild(metaPolicy);
      }

      // Add CSS if imported template
      if (state.template?.informations.imported) {
        const baseIframeImportedCss = document.createElement('link');
        baseIframeCss.setAttribute('rel', 'stylesheet');
        baseIframeCss.setAttribute('data-do-not-rewrite-url', '1');
        baseIframeCss.setAttribute('href', '/css/template-builder/baseIframeImported.css');
        content.value.head.appendChild(baseIframeImportedCss);
      }

      // Add template to content
      // add stylesheets to head
      getActiveTemplateStylesheets().forEach((stylesheet: HTMLStyleElement) => {
        if (content.value) content.value.head.appendChild(stylesheet);
      });

      // Add element ID on body (design element)
      if (isEmailTemplate(state.template?.type)) {
        const designId = state.template?.sections.filter((section: Section) => section.id_template_elements === state.template?.content.design)[0].id_template_elements;
        const design = state.template?.sections.find((section: Section) => section.id_template_elements === state.template?.content.design);
        if (designId && design) {
          content.value.body.setAttribute('data-spmelementid', designId.toString());
          const data = JSON.parse(design.data || '{}');
          if (data[HIDE_MOBILE_ATTRIBUTE]) {
            content.value.body.setAttribute(HIDE_MOBILE_ATTRIBUTE, 'true');
          } else {
            content.value.body.removeAttribute(HIDE_MOBILE_ATTRIBUTE);
          }
          const designSection = state.template?.sections.find((section: Section) => section.id_template_elements === asInt(designId));
          if (designSection) {
            const domParser = new DOMParser();
            const designDoc = domParser.parseFromString(designSection.html ?? '', 'text/html');

            const { body } = designDoc;
            const syncElementIdentifier = body.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER);
            const syncElementName = body.getAttribute(TEMPLATE_SYNC_ELEMENT_NAME);
            if (syncElementIdentifier) {
              content.value.body.setAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER, syncElementIdentifier);
            }
            if (syncElementName) {
              content.value.body.setAttribute(TEMPLATE_SYNC_ELEMENT_NAME, syncElementName);
            }
          }
        }
      }

      // If embed template, get selector and position in HTML code, store in state and insert at the right place in the page
      if (state.template?.type === TemplateTypeEnum.EMBED) {
        const selector = spmTemplate?.querySelector('#spm_body')?.getAttribute('data-target-element') ?? 'body';
        const position = spmTemplate?.querySelector('#spm_body')?.getAttribute('data-target-element-position') ?? 'append';
        setTemplateConfigurationKeyInState('embedSelector', selector);
        setTemplateConfigurationKeyInState('embedPosition', position);
        insertEmbedTemplateInDom(content.value, spmTemplate, selector, position);
      } else {
        content.value.body.appendChild(spmTemplate);
      }

      content.value = templateChecksOnOpening(content.value);
      const design = state.template?.sections.find((section: Section) => section.type === TemplateStructureEnum.DESIGN);
      if (design) {
        const designData = JSON.parse(design.data || '{}');
        if (designData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]) {
          content.value.body.setAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER, designData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]);
        }
        if (designData[TEMPLATE_SYNC_ELEMENT_NAME]) {
          content.value.body.setAttribute(TEMPLATE_SYNC_ELEMENT_NAME, designData[TEMPLATE_SYNC_ELEMENT_NAME]);
        }
      }
    };

    /**
     * Cancel select event on iframe's document
     */
    const interceptSelectsInDocument = () => {
      const template = getTemplateIframeDocument();

      if (template) {
        template.body.addEventListener('mousedown', (e) => {
          let target = e.target as HTMLElement;

          if (target) {
            // Check if target is a link or if one of its parents is a link
            while (target !== document.documentElement && target) {
              if (target.tagName && target.tagName.toLowerCase() === 'select') {
                // Prevent redirect
                e.preventDefault();
                e.stopPropagation();

                break;
              }

              target = target.parentNode as HTMLElement;
            }
          }
        });
      }
    };

    const interceptClicksOnImportedDocument = () => {
      const template = getTemplateIframeDocument();

      if (template) {
        template.body.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;

          if (target) {
            let targetLink = target;
            // Check if target is a link or if one of its parents is a link
            while (targetLink && targetLink.tagName.toLowerCase() !== 'body' && targetLink.tagName.toLowerCase() !== 'spm-template') {
              if (targetLink.tagName.toLowerCase() === 'a') {
                // Prevent redirect
                e.preventDefault();
                e.stopPropagation();
                break;
              }

              targetLink = targetLink.parentElement as HTMLElement;
            }

            const translationId = getTranslationIdFromImportedTemplateElement(target);
            if (translationId) {
              setSelectedTranslationId(translationId);
            }
          }

          return true;
        });
      }
    };
    const funnelsLoading = computed(() => store.getters['liveEditor/getFunnelsLoading']);
    let intervalId: number | null | ReturnType<typeof setInterval> = null; // Garde l'intervalle global
    const onLoad = () => {
      // Si l'intervalle est déjà en cours, ne rien faire
      if (intervalId !== null) {
        return;
      }
      loadDebugTickCount.value = 0;
      // Lancer l'intervalle une seule fois
      intervalId = setInterval(async () => {
        const template = getTemplateIframeDocument();
        loadDebugTickCount.value += 1;
        if (loadDebugTickCount.value === 5) {
          // no-op
        }
        if (iframe.value && srcDoc.value && template && template.body) {
          clearInterval(intervalId as unknown as number); // 💡 Cast pour compatibilité TS
          intervalId = null; // Réinitialiser la variable globale
          resetIframe();
          if (!isSmsTemplate(state.template?.type) && !isPushTemplate(state.template?.type) && !isFacebookTemplate(state.template?.type) && !isMobile.value) {
            resetTemplateToDesktop();
          }
          if (isDisplayTemplate(state.template?.type)) {
            applyPostLoadingChecksForDisplay();
            interceptSelectsInDocument();
          } else if (isEmailTemplate(state.template?.type)) {
            applyPostLoadingChecksForEmail();
          }

          // Prevent redirect on all link clicks inside the iframe (safety net for all template types)
          interceptClicksOnImportedDocument();
          showIframeTemplateWrapper();

          // Load push notification default icon URL for variable resolution
          if (isPushTemplate(state.template?.type)) {
            await initPushDefaultIconUrl(UserState.activeShop?.id ?? 0);
          }

          // Resolve variable expressions in image src attributes for preview display
          processVariableImagesOnLoad(template);

          context.emit('on-iframe-loaded', () => {
            if (!funnelsLoading.value) {
              loading.value = false;
            }
          });
        }
      }, 500);
    };

    watch(() => props.templateId, (value, oldValue) => {
      if (value !== oldValue) {
        loading.value = true;

        // Reset iframe content
        content.value = null;

        // Recreate iframe content
        createIframeContent();
      }
    }, { immediate: true });

    watch(funnelsLoading, () => {
      if (!funnelsLoading.value) {
        loading.value = false;
      }
    });

    const handleIFrameEvent = (event: any) => {
      if (event.type === 'mouseup') {
        store.commit('liveEditor/setIsSliderDragged', false);
      }
    };

    onBeforeUnmount(() => {
      if (intervalId) {
        clearInterval(intervalId as unknown as number);
        intervalId = null;
      }
    });

    return {
      loading,
      templateTitle,
      srcDoc,
      onLoad,
      handleIFrameEvent,
      isMobile,
      isDisplay,
      wrapperComponent,
      iframeStyle,
    };
  },
});
